import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    ReferenceInput,
    SelectInput,
    BooleanInput,
    required,
    email,
    useTranslate,
    Toolbar,
    SaveButton,
    DeleteButton,
    useRedirect,
    useNotify,
    useGetIdentity,
} from 'react-admin';
import { RfidUserGroupsField } from './RfidUserGroupsField';
import { Box, Typography, Card, CardContent, IconButton } from '@mui/material';
import { Person, Settings, ArrowBack } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const RFID_USER_KNOWN_FIELDS = new Set([
    'vendorId', 'email', 'phone', 'firstName', 'lastName', 'displayName', 'isActive',
]);

const mapRfidUserBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const rfidUserFieldLabelKey = (field: string): string =>
    RFID_USER_KNOWN_FIELDS.has(field)
        ? `resources.rfid-users.fields.${field}`
        : field;

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            display: 'flex',
            justifyContent: 'space-between',
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
        <DeleteButton
            sx={{
                color: '#D32F2F',
                '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/rfid-users')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.rfid-users.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.rfid-users.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const RfidUsersEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const validateEmail = email();
    const validateDisplayName = required(translate('ra.validation.required'));
    const { data: identity } = useGetIdentity();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    // Vendor-scope: backend ignores vendorId on update, but we still restrict
    // the dropdown so a scoped admin doesn't enumerate other merchants' names.
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.rfid-users.notifications.updated', { type: 'success' });
                    redirect('/rfid-users');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapRfidUserBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(rfidUserFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Person sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.rfid-users.sections.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <TextInput
                                    source="id"
                                    disabled
                                    fullWidth
                                    label={translate('resources.rfid-users.fields.id')}
                                />

                                <ReferenceInput
                                    source="vendorId"
                                    reference="vendors"
                                    label={translate('resources.rfid-users.fields.vendorId')}
                                    filter={isVendorScoped ? { id: actorVendorIds } : undefined}
                                >
                                    <SelectInput
                                        optionText="name"
                                        validate={required()}
                                        fullWidth
                                        readOnly={isVendorScoped}
                                        helperText={translate('resources.rfid-users.edit.vendorHelperText') ||
                                                  'Select the vendor/company this user belongs to'}
                                    />
                                </ReferenceInput>

                                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                    <TextInput
                                        source="firstName"
                                        label={translate('resources.rfid-users.fields.firstName')}
                                        fullWidth
                                    />

                                    <TextInput
                                        source="lastName"
                                        label={translate('resources.rfid-users.fields.lastName')}
                                        fullWidth
                                    />
                                </Box>

                                <TextInput
                                    source="displayName"
                                    label={translate('resources.rfid-users.fields.displayName') || 'Display Name'}
                                    validate={validateDisplayName}
                                    fullWidth
                                    helperText={translate('resources.rfid-users.edit.displayNameHelperText') ||
                                              'This name will be shown on charging sessions and reports'}
                                />

                                <TextInput
                                    source="email"
                                    label={translate('resources.rfid-users.fields.email')}
                                    validate={validateEmail}
                                    fullWidth
                                    helperText={translate('resources.rfid-users.edit.emailHelperText') ||
                                              'Optional - for notifications and receipts'}
                                />

                                <TextInput
                                    source="phone"
                                    label={translate('resources.rfid-users.fields.phone')}
                                    fullWidth
                                    helperText={translate('resources.rfid-users.edit.phoneHelperText') ||
                                              'Optional - for SMS notifications'}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Groups Section */}
                    <RfidUserGroupsField />

                    {/* Settings Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.rfid-users.sections.settings') || 'Settings'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <BooleanInput
                                    source="isActive"
                                    label={translate('resources.rfid-users.fields.isActive')}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};